import type { ShallowRef } from 'vue'
import { useElementSize, useEventListener } from '@vueuse/core'

const HIDDEN_CLASS = 'kft-header-hidden'
// ignore scroll jitter (trackpad inertia, iOS bounce) below this delta
const SCROLL_DELTA_THRESHOLD = 8

/**
 * Hide the sticky layout header while scrolling down and reveal it as soon as
 * the user scrolls up.
 *
 * The header is slid out by animating its sticky `top` to minus its own
 * height rather than with a `transform`: the header contains `position: fixed`
 * descendants (mobile navigation drawer), and a transform would turn the
 * header into their containing block.
 *
 * Also toggles `html.kft-header-hidden` so other sticky elements that sit
 * below the header (e.g. the listing filters bar) can move up with it.
 */
export const useAutoHideHeader = (
  header: Readonly<ShallowRef<HTMLElement | null>>
) => {
  const isHidden = useState('autoHideHeaderHidden', () => false)
  const { height } = useElementSize(header, undefined, { box: 'border-box' })

  const headerStyle = computed(() => ({
    top: isHidden.value ? `-${Math.ceil(height.value)}px` : '0px',
  }))

  if (import.meta.client) {
    const route = useRoute()
    let lastScrollY = window.scrollY
    let lastScrollHeight = document.documentElement.scrollHeight
    let ticking = false
    // touch browsers leave :hover stuck on the last tapped element, which would
    // pin the header after any tap on it, so only trust hover on real pointers
    const canHover = window.matchMedia('(hover: hover)')

    const shouldStayVisible = () => {
      const el = header.value
      if (!el) return true
      // body scroll is locked by an open drawer / sidebar
      if (document.body.classList.contains('prevent-scroll')) return true
      // keep it while the user interacts with it (search input, megamenu hover)
      return (
        (canHover.matches && el.matches(':hover')) ||
        el.contains(document.activeElement)
      )
    }

    const update = () => {
      ticking = false
      const scrollY = Math.max(window.scrollY, 0)
      const delta = scrollY - lastScrollY
      const scrollHeight = document.documentElement.scrollHeight

      const hasResized = scrollHeight !== lastScrollHeight
      lastScrollHeight = scrollHeight

      // content resized (e.g. search results rendering) and the browser pulled
      // the scroll position up to compensate: not a user scroll-up, just resync
      if (hasResized && delta < 0) {
        lastScrollY = scrollY
        return
      }

      if (scrollY <= height.value) {
        isHidden.value = false
      } else if (Math.abs(delta) < SCROLL_DELTA_THRESHOLD) {
        return
      } else if (delta > 0) {
        isHidden.value = !shouldStayVisible()
      } else {
        isHidden.value = false
      }
      lastScrollY = scrollY
    }

    useEventListener(
      window,
      'scroll',
      () => {
        if (ticking) return
        ticking = true
        requestAnimationFrame(update)
      },
      { passive: true }
    )

    watch(
      () => route.path,
      () => {
        isHidden.value = false
        lastScrollY = window.scrollY
        lastScrollHeight = document.documentElement.scrollHeight
      }
    )

    watch(
      isHidden,
      (hidden) =>
        document.documentElement.classList.toggle(HIDDEN_CLASS, hidden),
      { immediate: true }
    )

    onScopeDispose(() => {
      isHidden.value = false
      document.documentElement.classList.remove(HIDDEN_CLASS)
    })
  }

  return { isHeaderHidden: isHidden, headerStyle }
}
